import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo } from 'react'
import { type ActionItem, Actions, Typography, variantActionsProps } from 'shared/ui'
import { assignInbox, getPhoneActions } from 'entities/Phone/ui/actions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useEventSettingsNumbersUnassignedRefreshFinished } from 'entities/Phone/events/EventSettingsNumbersUnassignedRefreshFinished'
import { type RowNumber } from 'entities/Phone/model/Table/RowNumber'
import { UnassignedTableStore } from 'pages/settings/pages/numbers/ui/UnassignedNumbers/store/UnassignedNumbersStore'
import { NumbersTable } from 'pages/settings/pages/numbers/ui/NumbersTable'
import { ImportTwilioNumbersAction } from 'pages/settings/pages/numbers/ui/ImportTwilioNumbers/ImportTwilioNumbersAction'
import { useEventNumbersInboxBulkCreationFinished } from 'pages/settings/pages/numbers/events/EventNumbersInboxBulkCreationFinished'
import { RejectAssignedNumbersAlert } from 'pages/settings/ui/RejectAssignedNumbersAlert/RejectAssignedNumbersAlert'
import { useEventOrganizationRestrictionRemoved } from 'pages/settings/pages/numbers/events/EventOrganizationRestrictionRemoved'
import styles from './styles.module.scss'

type IUnassignedNumbersProps = {
  onSuccessAssignNumber: () => void
  manageNumberWrapper: (fn: () => void, options?: { skipCanceledCheck?: boolean }) => void
}

export const UnassignedNumbers = observer(
  ({ onSuccessAssignNumber, manageNumberWrapper }: IUnassignedNumbersProps) => {
    const entities = useEntitiesContext()
    const { organizationStore, subscriptionStore } = entities
    const {
      init,
      numbersListStore,
      rejectAssignedNumbers,
      rejectMessage,
      clearRejectAssignedNumbers,
      airCallLink,
      onCloseAssignInboxModalModal,
    } = useMemo(() => new UnassignedTableStore(onSuccessAssignNumber, entities), [])

    const isPAYGPlan = subscriptionStore.isPAYGPlan

    const getIsRowDisabled = useCallback((row: RowNumber) => !!row.origin.disabled, [])
    const getIsRowCheckboxDisabled = (row: RowNumber) => row.origin.isLandline

    const getRowActions = (row: RowNumber): ActionItem[] => {
      return getPhoneActions(row.origin, entities, {
        loadData: numbersListStore.loadData,
        onCloseAssignInboxModalModal,
      })
    }

    useEventSettingsNumbersUnassignedRefreshFinished(() => {
      numbersListStore.loadData()
      entities.subscriptionStore.init()
    })
    useEventNumbersInboxBulkCreationFinished(numbersListStore.loadData)

    useEventOrganizationRestrictionRemoved(() => {
      numbersListStore.loadData()
    })

    useEffect(() => {
      init()
      return clearRejectAssignedNumbers
    }, [])

    const isImportTwilioNumbers =
      (!isPAYGPlan && organizationStore.isTwilioSubAccountNumberVendor) ||
      organizationStore.isTwilioNumberVendor

    return (
      <>
        <RejectAssignedNumbersAlert
          airCallLink={airCallLink}
          rejectAssignedNumbers={rejectAssignedNumbers}
          clearRejectAssignedNumbers={clearRejectAssignedNumbers}
          rejectMessage={rejectMessage}
        />
        <NumbersTable
          ariaLabel={'UnassignedNumbers'}
          numbersListStore={numbersListStore}
          rightHeaderContent={
            isImportTwilioNumbers && (
              <ImportTwilioNumbersAction manageNumberWrapper={manageNumberWrapper} />
            )
          }
          hiddenColumns={['inboxName']}
          getRowActions={getRowActions}
          withCheckbox
          notFoundMessage={'No unassigned number found'}
          getIsRowDisabled={getIsRowDisabled}
          getIsRowCheckboxDisabled={getIsRowCheckboxDisabled}
          headerLeftContentProps={{
            actionsContent: (
              <Actions
                {...variantActionsProps.headerSmall}
                actions={[
                  assignInbox(
                    numbersListStore.tableStore.selectedRows.map((row) => row.origin.origin),
                    onCloseAssignInboxModalModal,
                    entities
                  ),
                ]}
              />
            ),
          }}
          emptyContent={
            isImportTwilioNumbers && (
              <div className={styles.empty}>
                <div className={styles.block}>
                  <Typography
                    ariaLabel={'Import Twilio numbers'}
                    variant={'body-lg-medium'}
                    marginBottom={8}
                  >
                    Import Twilio numbers
                  </Typography>
                  <Typography
                    ariaLabel={'desc'}
                    variant={'body-md-regular'}
                    color={'var(--content-primary-tertiary)'}
                    className={styles.desc}
                  >
                    If you have available Twilio numbers, you can <br /> bring them into Salesmsg
                  </Typography>
                </div>
                <ImportTwilioNumbersAction
                  variant={'button'}
                  manageNumberWrapper={manageNumberWrapper}
                />
              </div>
            )
          }
        />
      </>
    )
  }
)
